import React, { Component } from "react";
import { addMilliseconds } from "date-fns";
import { observer } from "mobx-react";

import { combineFilters } from "@formant/core/common/combineFilters";
import { plural } from "@formant/core/common/plural";
import { IAnnotation } from "@formant/core/model/IAnnotation";
import { IEvent } from "@formant/core/model/IEvent";
import { IEventOrAggregate } from "@formant/core/model/IEventOrAggregate";
import { IEventQuery } from "@formant/core/model/IEventQuery";
import { IFilter } from "@formant/core/model/IFilter";
import { LoadingIndicator } from "@formant/ui/component/common/LoadingIndicator";

import {
    seekFilterExcludedStreams,
    seekOptionToEventTypes,
    seekOptionToNoun,
} from "../../store/SeekOption";
import { eventStore, observeStore } from "../../stores";
import { Escape } from "../common/Escape";
import { Heading3 } from "../common/Heading3";
import { IconButton } from "../common/IconButton";
import { NoDataMessage } from "../common/NoDataMessage";
import { Scrollable } from "../common/Scrollable";
import { StandardButton } from "../common/StandardButton";
import { EventListItem } from "../EventList/EventListItem";
import * as styles from "./ListEvents.scss";
import { SeekButton } from "./SeekButton";

export interface IListEventsProps {
    context: IFilter;
    filter?: IFilter;
    onAnnotate?: (annotation: Partial<IAnnotation>) => void;
    onSeek?: (time: Date) => void;
    onSelectEvent?: (event: IEventOrAggregate) => void;
    title: string;
}

@observer
export class ListEvents extends Component<IListEventsProps> {
    private events: IEvent[] | undefined;

    public render() {
        const { onClickEventListItem, onClose } = this;
        const { context, filter, onAnnotate, onSeek, onSelectEvent, title } =
            this.props;
        const { seekOption } = observeStore;

        this.events = this.query || this.events;
        const events = this.events;

        const seekFilter = combineFilters(
            combineFilters(context, filter),
            seekFilterExcludedStreams,
        );

        const deviceImplied = context.deviceIds?.length === 1;
        const seekNoun = seekOptionToNoun(seekOption);

        return (
            <div className={styles["list-events"]}>
                <Heading3 className={styles.heading}>
                    <div className={styles.title}>{title}</div>
                    <IconButton
                        className={styles.close}
                        onClick={onClose}
                        icon="chevron-right"
                    />
                </Heading3>
                <Scrollable>
                    <LoadingIndicator visible={!events} />
                    {events?.map(event => (
                        <EventListItem
                            key={event.id}
                            event={event}
                            onSelect={onClickEventListItem}
                            hideDeviceColumn={deviceImplied}
                            minimal={true}
                        />
                    ))}
                    {events?.length === 0 && (
                        <NoDataMessage
                            asBanner={true}
                            className={styles["no-data"]}
                        >
                            <div className={styles.message}>
                                There are no{" "}
                                {plural(
                                    seekOption === "Data presence"
                                        ? "event"
                                        : seekNoun,
                                )}{" "}
                                in view
                            </div>
                            <div className={styles.seek}>
                                <SeekButton
                                    direction="previous"
                                    filter={seekFilter}
                                    onSeek={onSeek}
                                    onSelectEvent={onSelectEvent}
                                    seekOption={seekOption}
                                    data-tooltip={`Seek to previous ${seekNoun}`}
                                />
                                <div>Seek</div>
                                <SeekButton
                                    direction="next"
                                    filter={seekFilter}
                                    onSeek={onSeek}
                                    onSelectEvent={onSelectEvent}
                                    seekOption={seekOption}
                                    data-tooltip={`Seek to next ${seekNoun}`}
                                />
                            </div>
                        </NoDataMessage>
                    )}
                </Scrollable>
                {onAnnotate &&
                    ["Data presence", "Annotations"].includes(seekOption) && (
                        <div className={styles.footer}>
                            <StandardButton onClicked={onAnnotate}>
                                New annotation
                            </StandardButton>
                        </div>
                    )}
                <Escape onEsc={onClose} />
            </div>
        );
    }

    private get query() {
        const { context, filter } = this.props;
        const { range, seekOption, selectedAggregateEvent } = observeStore;
        const start = selectedAggregateEvent?.start || range[0];
        const end = selectedAggregateEvent?.end || range[1];
        const query: IEventQuery = {
            ...combineFilters(context, filter),
            start: start.toISOString(),
            end: addMilliseconds(end, 1).toISOString(),
            count: 40,
        };
        if (seekOption !== "Data presence") {
            query.eventTypes = seekOptionToEventTypes(seekOption);
        }
        return eventStore.query(query);
    }

    private onClickEventListItem = (event: IEvent) => {
        observeStore.viewEvent(event);
    };

    private onClose = () => {
        observeStore.closeEventSidebar();
    };
}
